import { useCallback, useMemo, useRef } from "react";
import _ from "lodash";
import { v4 as uuid } from "uuid";
import { debounceDelay, formCategories, presetSectionsCodeObject } from "@Helpers/constants";
import { formSectionType, TEMPLATE_SOURCE_MAIL_CREATION, TEMPLATE_SOURCE_MAIL_SIDEBAR } from "./constants";

interface UseFormPreviewLogicProps {
  formData: {
    template?: any[];
    [key: string]: any;
  };
  setFormData?: (updater: (prev: any) => any) => void;
  handleData?: (template: any[], sectionId: string, fieldId: string) => void;
  handleSaveCustomFields?: (fieldsData: any[]) => void;
  setUpdateTemplateAfterUse?: (updater: (prev: any) => any) => void;
  userDetails?: { _id?: string };
  debouncedSave?: any;
  resourceType?: string;
  templateSource?: string;
}

interface HandleCustomFieldDataParams {
  eachField: any;
  answerValue: any;
  fieldIndex: number;
  sectionIndex: number;
  tableRowIndex?: number;
  source?: string;
  file?: File;
}

export const useFormPreviewLogic = ({
  formData,
  setFormData,
  handleData,
  handleSaveCustomFields = () => {},
  setUpdateTemplateAfterUse,
  userDetails,
  resourceType,
  templateSource,
}: UseFormPreviewLogicProps) => {
  /**
   * Checks if a section is a preset section type that shows placeholder UI
   */
  const isPresetPlaceholderSection = useCallback((sectionCode: string): boolean => {
    return [
      presetSectionsCodeObject.SECTION_TYPE_QUANTITY,
      presetSectionsCodeObject.SECTION_TYPE_FORM,
      presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
      presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
      presetSectionsCodeObject.SECTION_TYPE_REVIEWS,
    ].includes(sectionCode);
  }, []);

  /**
   * Checks if a section is a task section
   */
  const isTaskSection = useCallback((sectionCode: string): boolean => {
    return sectionCode === presetSectionsCodeObject.SECTION_TYPE_TASK;
  }, []);

  /**
   * Checks if a section is a milestone section
   */
  const isMilestoneSection = useCallback((sectionCode: string): boolean => {
    return sectionCode === presetSectionsCodeObject.SECTION_TYPE_MILESTONE;
  }, []);

  /**
   * Checks if a section is a table section
   */
  const isTableSection = useCallback((sectionCode: string): boolean => {
    return sectionCode === presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION;
  }, []);

  /**
   * Checks if a section is available for mobile
   */
  const isSectionAvailableForMobile = useCallback((sectionCode: string): boolean => {
    const unavailableSections = [
      presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
      presetSectionsCodeObject.SECTION_TYPE_FORM,
      presetSectionsCodeObject.SECTION_TYPE_MILESTONE,
      presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
    ];
    return !unavailableSections.includes(sectionCode);
  }, []);

  const handleDataRef = useRef(handleData);
  const handleSaveCustomFieldsRef = useRef(handleSaveCustomFields);
  handleDataRef.current = handleData;
  handleSaveCustomFieldsRef.current = handleSaveCustomFields;

  /**
   * Handles updating field data when user inputs a value
   */
  const debouncedSave = useMemo(
    () =>
      _.debounce((modifiedTemplate, _id, sectionIndex, fieldIndex, answerValue, previousAns) => {
        if (handleDataRef.current) {
          handleDataRef.current(modifiedTemplate, modifiedTemplate[sectionIndex]._id, _id);
        }

        const fieldsData = [
          {
            _id,
            value: answerValue,
            questionType: modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]?.fieldType,
            questionValue: modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]?.questionValue,
            sectionId: modifiedTemplate[sectionIndex]._id,
            prevAnswer: previousAns,
          },
        ];
        handleSaveCustomFieldsRef.current(fieldsData);
      }, debounceDelay),
    [],
  );

  const filterFields = (field: any) => {
    if (resourceType === formCategories.mailBox && templateSource) {
      if (templateSource === TEMPLATE_SOURCE_MAIL_CREATION) return field.editableInForm ?? true;
      if (templateSource === TEMPLATE_SOURCE_MAIL_SIDEBAR) return field.isMetadata ?? true;
      return false;
    }
    return true;
  };

  const handleCustomFieldData = ({
    eachField,
    answerValue,
    fieldIndex,
    sectionIndex,
    tableRowIndex = 0,
    source,
  }: HandleCustomFieldDataParams) => {
    const { _id } = eachField;
    const modifiedTemplate = _.cloneDeep(formData?.template);

    if (!modifiedTemplate?.[sectionIndex]) return;

    const previousAns = modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]?.answer;

    if (source === formSectionType.TABLE_SECTION) {
      const modifiedTableDataSource = modifiedTemplate[sectionIndex].tableDataSource?.map((row: any, index: number) => {
        if (index === tableRowIndex) {
          row[_id] = {
            createdDate: Date.now(),
            createdBy: userDetails?._id,
            value: answerValue,
          };
        }
        return row;
      });
      modifiedTemplate[sectionIndex].tableDataSource = modifiedTableDataSource;

      if (setUpdateTemplateAfterUse) {
        setUpdateTemplateAfterUse((prev: any) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...(prev?.CONFIGURE_SECTION || []),
            {
              fieldType: "tableDataSource",
              tableDataSource: modifiedTableDataSource,
              sectionId: formData.template?.[sectionIndex]?._id,
            },
          ],
        }));
      }
    } else if (modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]?.answer) {
      modifiedTemplate[sectionIndex].questionFields[fieldIndex].answer = {
        ...modifiedTemplate[sectionIndex].questionFields[fieldIndex].answer,
        value: answerValue,
      };
    } else if (modifiedTemplate[sectionIndex].questionFields?.[fieldIndex]) {
      modifiedTemplate[sectionIndex].questionFields[fieldIndex].answer = {
        value: answerValue,
      };
    }

    if (setFormData) {
      setFormData((prev: any) => ({
        ...prev,
        template: modifiedTemplate,
      }));
    }
    if (setUpdateTemplateAfterUse) {
      setUpdateTemplateAfterUse((prev: any) => ({
        ...prev,
        CONFIGURE_QUESTION: [
          ...(prev?.CONFIGURE_QUESTION || []).filter((q: any) => q.questionId != eachField._id),
          {
            questionId: eachField._id,
            configuration: { answer: { value: answerValue } },
            sectionId: modifiedTemplate[sectionIndex]._id,
          },
        ],
      }));
    }
    debouncedSave(modifiedTemplate, _id, sectionIndex, fieldIndex, answerValue, previousAns);
  };

  /**
   * Adds a new row to a table section
   */
  const handleAddTableRow = useCallback(
    ({ sectionIndex }: { sectionIndex: number }) => {
      if (!setFormData || !formData?.template?.[sectionIndex]) return;

      const newObjRow: any = {
        rowId: uuid(),
        isPreFilledRow: true,
      };

      formData.template[sectionIndex].questionFields?.forEach((field: any) => {
        newObjRow[field._id] = {};
      });

      const modifiedTemplate = [...formData.template];

      let newTableDataSource: any[] = [];
      if (modifiedTemplate[sectionIndex].tableDataSource) {
        newTableDataSource = [...modifiedTemplate[sectionIndex].tableDataSource, newObjRow];
      } else {
        newTableDataSource = [newObjRow];
      }
      modifiedTemplate[sectionIndex].tableDataSource = newTableDataSource;

      setFormData((prev: any) => ({
        ...prev,
        template: modifiedTemplate,
      }));

      if (setUpdateTemplateAfterUse) {
        setUpdateTemplateAfterUse((prev: any) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...(prev?.CONFIGURE_SECTION || []),
            {
              fieldType: "tableDataSource",
              tableDataSource: newTableDataSource,
              sectionId: formData.template?.[sectionIndex]?._id,
            },
          ],
        }));
      }
    },
    [formData?.template, setFormData, setUpdateTemplateAfterUse],
  );

  /**
   * Deletes a row from a table section
   */
  const handleDeleteTableRow = useCallback(
    ({ sectionIndex, rowIndex }: { sectionIndex: number; rowIndex: number }) => {
      if (!setFormData || !formData?.template?.[sectionIndex]) return;

      const modifiedTemplate = [...formData.template];
      const dataSource = [...(modifiedTemplate[sectionIndex].tableDataSource || [])];

      dataSource.splice(rowIndex, 1);
      modifiedTemplate[sectionIndex].tableDataSource = dataSource;

      setFormData((prev: any) => ({
        ...prev,
        template: modifiedTemplate,
      }));

      if (setUpdateTemplateAfterUse) {
        setUpdateTemplateAfterUse((prev: any) => ({
          ...prev,
          CONFIGURE_SECTION: [
            ...(prev?.CONFIGURE_SECTION || []),
            {
              fieldType: "tableDataSource",
              tableDataSource: dataSource,
              sectionId: formData.template?.[sectionIndex]?._id,
            },
          ],
        }));
      }
    },
    [formData?.template, setFormData, setUpdateTemplateAfterUse],
  );

  /**
   * Determines the section type for rendering
   */
  const getSectionType = useCallback(
    (section: any): "preset-placeholder" | "task" | "milestone" | "table" | "default" | "unavailable-mobile" => {
      if (!section?.sectionCode) return "default";

      if (isPresetPlaceholderSection(section.sectionCode)) return "preset-placeholder";
      if (isTaskSection(section.sectionCode)) return "task";
      if (isMilestoneSection(section.sectionCode)) return "milestone";
      if (isTableSection(section.sectionCode)) return "table";

      return "default";
    },
    [isPresetPlaceholderSection, isTaskSection, isMilestoneSection, isTableSection],
  );

  return {
    filterFields,
    handleCustomFieldData,
    handleAddTableRow,
    handleDeleteTableRow,
    getSectionType,
    isPresetPlaceholderSection,
    isTaskSection,
    isMilestoneSection,
    isTableSection,
    isSectionAvailableForMobile,
  };
};

export default useFormPreviewLogic;
